import { css } from 'lit';

// Customer-side brand system — "Bright morning, premium loyalty".
//
// Target: fresh, welcoming, premium loyalty programme aesthetic — think
// Marriott Bonvoy / Sephora Beauty Insider / Starbucks Rewards. Gold is
// the primary because it's a) on-brand for Pag-asenso, b) the universal
// loyalty colour signal, c) warmer than rose without being childish.
//
// Colour roles:
//   - Gold (`--sun` / `--sun-deep`): PRIMARY accent. Hero highlights,
//     primary CTAs, eyebrow pills, focus rings, gradient cards.
//   - Coral (`--coral`): SECONDARY, reserved for "heart / love" moments
//     — the heart-fill icon (loyalty mark), the "Join" CTA, the heart
//     stamp on success. Used sparingly so it stays meaningful.
//   - Navy (`--brand-navy`): brand wordmark + serious UI ink.
//   - Mint: success / fresh.
export const customerBrandStyles = css`
  :host {
    /* === Type ============================================ */
    --font-display: 'Bricolage Grotesque', system-ui, -apple-system, sans-serif;
    --font-body: 'Bricolage Grotesque', system-ui, -apple-system, sans-serif;
    --font-numeric: 'Bricolage Grotesque', system-ui, sans-serif;

    /* === Surfaces ======================================== */
    --canvas: #ffffff; /* main background */
    --canvas-soft: #faf8f6; /* subtle off-white */
    --line: #ececf2; /* hairline */
    --line-soft: #f3f3f8;

    /* Pastel card tints */
    --tint-coral: #ffeaf1;
    --tint-sun: #fff4d4;
    --tint-mint: #dbf6e7;
    --tint-sky: #e2efff;

    /* === Ink ============================================== */
    --ink: #0c1322; /* near-black for high contrast */
    --ink-soft: #4a5267;
    --ink-faint: #8b93a8;

    /* === Brand identity (Pag-asenso) ===================== */
    --brand-navy: #0f2a44;
    --brand-gold: #d99820; /* deep gold — legible on white */

    /* === Vibrant accents ================================== */
    --coral: #ff4f8b; /* primary action / loyalty */
    --coral-deep: #e62a72;
    --coral-glow: rgba(255, 79, 139, 0.22);

    --sun: #ffc83d; /* energy / highlight */
    --sun-deep: #e9a800;
    --sun-glow: rgba(255, 200, 61, 0.28);

    --mint: #2ecc8b; /* success / fresh */
    --mint-deep: #1faa6f;
    --mint-glow: rgba(46, 204, 139, 0.22);

    /* === Filipino cultural earth + tropical palette ====== */
    /* Used in loyalty-card variants + supporting accents so the
       system feels locally rooted, not generic SaaS. */
    --leaf: #4a8c5a; /* lush tropical green / malong */
    --leaf-deep: #2f6b3f;
    --leaf-tint: #dceedb;

    --mocha: #6b4423; /* warm earth brown / rich loam */
    --mocha-soft: #a47554;
    --mocha-tint: #ecddca;

    --clay: #c25a3c; /* burnt terracotta / banderita */
    --clay-tint: #f6d9cd;

    --festive: #c84545; /* banderita red — celebration only */

    /* === Shadows ========================================== */
    --shadow-soft:
      0 2px 8px rgba(12, 19, 34, 0.05), 0 1px 2px rgba(12, 19, 34, 0.04);
    --shadow-lift:
      0 8px 24px rgba(12, 19, 34, 0.08), 0 2px 6px rgba(12, 19, 34, 0.04);
    --shadow-pop:
      0 16px 40px rgba(12, 19, 34, 0.14), 0 4px 12px rgba(12, 19, 34, 0.06);

    /* Tinted glow shadows for vibrant cards */
    --shadow-coral: 0 16px 36px rgba(255, 79, 139, 0.32);
    --shadow-sun: 0 16px 36px rgba(255, 200, 61, 0.32);
    --shadow-mint: 0 16px 36px rgba(46, 204, 139, 0.32);
  }

  /* Dark mode — same vibrancy, dimmer canvas, slightly muted accents. */
  :host-context(.sl-theme-dark) {
    --canvas: #0f1320;
    --canvas-soft: #161b2b;
    --line: #232a3c;
    --line-soft: #1c2230;
    --tint-coral: rgba(255, 79, 139, 0.1);
    --tint-sun: rgba(255, 200, 61, 0.12);
    --tint-mint: rgba(46, 204, 139, 0.12);
    --tint-sky: rgba(107, 184, 255, 0.12);
    --ink: #f4f5fa;
    --ink-soft: #c1c6d6;
    --ink-faint: #7a8095;
    --brand-navy: #ffffff;
    --brand-gold: #ffc83d;
    --shadow-soft: 0 2px 8px rgba(0, 0, 0, 0.4);
    --shadow-lift: 0 8px 24px rgba(0, 0, 0, 0.5);
    --shadow-pop: 0 16px 40px rgba(0, 0, 0, 0.6);
  }

  /* ===================================================== */
  /* Page chrome                                            */
  /* ===================================================== */
  .page {
    min-height: 100vh;
    background: var(--canvas);
    color: var(--ink);
    font-family: var(--font-body);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: hidden;
  }

  /* Soft atmospheric blobs — large, blurred, low-opacity. Used as
     decorative backdrop on hero pages. Two blobs (coral + sun) feel
     "morning sun rising." */
  .blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    pointer-events: none;
    z-index: 0;
    opacity: 0.65;
  }
  .blob.coral {
    background: var(--coral);
  }
  .blob.sun {
    background: var(--sun);
  }
  .blob.mint {
    background: var(--mint);
  }

  /* ===================================================== */
  /* Brand wordmark (navy + gold, per the official SVG)     */
  /* ===================================================== */
  .page-brand {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 18px clamp(16px, 4vw, 24px);
    flex: none;
    position: relative;
    z-index: 2;
  }
  .page-brand-mark {
    display: inline-flex;
    align-items: center;
    gap: 0.7rem;
    text-decoration: none;
    color: inherit;
  }
  .page-brand-mark img {
    width: 44px;
    height: 44px;
    border-radius: 11px;
  }
  .page-brand-wordmark {
    font-family: var(--font-display);
    font-weight: 800;
    font-size: 22px;
    letter-spacing: -0.025em;
    color: var(--brand-navy);
    line-height: 1;
  }
  .page-brand-wordmark span {
    color: var(--brand-gold);
    font-weight: 800;
  }

  /* ===================================================== */
  /* Display headline                                       */
  /* ===================================================== */
  .display {
    font-family: var(--font-display);
    font-weight: 800;
    line-height: 1.02;
    letter-spacing: -0.035em;
    color: var(--ink);
    margin: 0;
  }
  .display .accent {
    color: var(--coral);
  }
  .display .wave {
    display: inline-block;
    font-weight: 400;
    transform-origin: 70% 70%;
    animation: wave 2.4s ease-in-out infinite;
  }
  @keyframes wave {
    0%,
    60%,
    100% {
      transform: rotate(0deg);
    }
    10%,
    30% {
      transform: rotate(14deg);
    }
    20% {
      transform: rotate(-8deg);
    }
    40% {
      transform: rotate(14deg);
    }
    50% {
      transform: rotate(-4deg);
    }
  }

  /* ===================================================== */
  /* Pills + badges                                         */
  /* ===================================================== */
  .pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    background: var(--tint-coral);
    color: var(--coral-deep);
    font-family: var(--font-body);
    font-weight: 700;
    font-size: 11px;
    letter-spacing: 0.04em;
    border-radius: 999px;
    line-height: 1.4;
    white-space: nowrap;
  }
  .pill.sun {
    background: var(--tint-sun);
    color: var(--sun-deep);
  }
  .pill.mint {
    background: var(--tint-mint);
    color: var(--mint-deep);
  }
  .pill sl-icon {
    font-size: 12px;
  }

  /* ===================================================== */
  /* Buttons (pill, solid bright)                           */
  /* ===================================================== */
  .pa-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 14px 22px;
    background: var(--ink);
    color: var(--canvas);
    border: none;
    border-radius: 999px;
    font-family: var(--font-body);
    font-weight: 700;
    font-size: 15px;
    letter-spacing: -0.005em;
    cursor: pointer;
    text-decoration: none;
    box-shadow: var(--shadow-soft);
    transition:
      transform 160ms cubic-bezier(0.2, 0.7, 0.2, 1),
      box-shadow 160ms ease,
      background 160ms ease;
    line-height: 1;
  }
  .pa-btn:hover {
    transform: translateY(-1px);
    box-shadow: var(--shadow-lift);
  }
  .pa-btn:active {
    transform: translateY(0);
  }
  .pa-btn[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }
  .pa-btn.coral {
    background: var(--coral);
    color: white;
    box-shadow: 0 6px 18px var(--coral-glow);
  }
  .pa-btn.coral:hover {
    background: var(--coral-deep);
    box-shadow: 0 10px 28px var(--coral-glow);
  }
  .pa-btn.sun {
    background: var(--sun);
    color: var(--ink);
    box-shadow: 0 6px 18px var(--sun-glow);
  }
  .pa-btn.sun:hover {
    background: var(--sun-deep);
  }
  .pa-btn.ghost {
    background: var(--canvas-soft);
    color: var(--ink);
    box-shadow: none;
    border: 1px solid var(--line);
  }
  .pa-btn.ghost:hover {
    background: var(--canvas);
    box-shadow: var(--shadow-soft);
  }
  .pa-btn sl-icon {
    font-size: 17px;
  }

  /* ===================================================== */
  /* Eyebrow label                                          */
  /* ===================================================== */
  .eyebrow {
    font-family: var(--font-body);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--ink-faint);
  }

  /* ===================================================== */
  /* Entry animations                                       */
  /* ===================================================== */
  @keyframes pa-rise {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .pa-rise {
    animation: pa-rise 520ms cubic-bezier(0.16, 0.84, 0.44, 1) both;
  }
  .pa-rise-2 {
    animation-delay: 70ms;
  }
  .pa-rise-3 {
    animation-delay: 140ms;
  }
  .pa-rise-4 {
    animation-delay: 210ms;
  }
  .pa-rise-5 {
    animation-delay: 280ms;
  }
`;
